<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide';
import { PaginationFirst, PaginationLast, PaginationNext, PaginationPrev, PaginationRoot } from 'reka-ui';
import { computed, nextTick, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import { useI18n } from '@/composables/i18n';
import { cn } from '@/utils/shadcn/lib/utils';
import PageInput from './internal/PageInput.vue';
import PageList from './internal/PageList.vue';
import PageSizeSelect from './internal/PageSizeSelect.vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<{
    /** Number of items across all pages. */
    total: number
    /** Selectable page sizes. Empty hides the page size select. */
    pageSizes?: number[]
    /** Show the visible range, e.g. "26 – 50 von 250". */
    showCounter?: boolean
    /**
     * `pages` always lists the page numbers, `input` always offers the page field,
     * `auto` switches to the field once there are more pages than the threshold.
     */
    mode?: 'auto' | 'pages' | 'input'
    disabled?: boolean
    class?: HTMLAttributes['class']
}>(), {
    pageSizes: () => [],
    showCounter: true,
    mode: 'auto',
    disabled: false,
    class: '',
});

const page = defineModel<number>('page', { default: 1 });
const itemsPerPage = defineModel<number>('itemsPerPage', { default: 25 });

/** Page count above which `auto` prefers the page field over the number list. */
const PAGE_INPUT_THRESHOLD = 10;

/** Page numbers shown on either side of the current page. */
const SIBLING_COUNT = 1;

const { i18n } = useI18n();

const pageCount = computed(() => Math.max(1, Math.ceil(props.total / (itemsPerPage.value || 1))));

// A shrinking result set must not strand the caller on a page that no longer exists. Page size
// changes are left to the watcher below, which resets to page 1 anyway; clamping here as well
// would emit an intermediate page first.
watch(() => props.total, () => {
    if (page.value > pageCount.value) {
        page.value = pageCount.value;
    } else if (page.value < 1) {
        page.value = 1;
    }
}, { immediate: true });

/*
 * A page size change is not a step through the list: every number now stands for a different slice
 * and the jump back to page 1 is not a move the reader could follow, so the list swaps outright
 * instead of animating. Not immediate: the page size the component starts with is not a change.
 */
const animateList = ref(true);

watch(itemsPerPage, async () => {
    page.value = 1;
    animateList.value = false;

    // Resumes once the rebuilt list has been patched in, which is the render that must stay still.
    await nextTick();
    animateList.value = true;
});

/**
 * `list` and `field` are decided here and rendered alone; `responsive` puts both in the DOM and
 * leaves the choice to the container query below.
 */
const branch = computed<'list' | 'field' | 'responsive'>(() => {
    if (props.mode === 'pages') {
        return 'list';
    }

    if (props.mode === 'input') {
        return 'field';
    }

    return pageCount.value <= PAGE_INPUT_THRESHOLD ? 'responsive' : 'field';
});

// The number list always carries the first and last page (`show-edges`), so jump-to-edge buttons
// would only duplicate them. They earn their place next to the page field, which has no edges.
const listClass = computed(() => (branch.value === 'responsive' ? '@max-[30rem]/pagination:hidden' : ''));
const fieldClass = computed(() => (branch.value === 'responsive' ? 'hidden @max-[30rem]/pagination:flex' : 'flex'));
const edgeClass = computed(() => (branch.value === 'responsive' ? 'hidden @max-[30rem]/pagination:inline-flex' : ''));

// Nothing to navigate on a single page, so the navigation goes rather than sit there inert.
const showNavigation = computed(() => pageCount.value > 1);

// The select stays even on a single page — without it there is no way back from "10 per page".
const showPageSizeSelect = computed(() => props.pageSizes.length > 0);

const hasContent = computed(() => showNavigation.value || props.showCounter || showPageSizeSelect.value);

const counterText = computed(() => {
    const from = props.total === 0 ? 0 : (page.value - 1) * itemsPerPage.value + 1;
    const to = Math.min(page.value * itemsPerPage.value, props.total);

    return i18n.t('%{count} von %{length}', { count: `${from} – ${to}`, length: props.total });
});
</script>

<template>
    <PaginationRoot
        v-if="hasContent"
        v-model:page="page"
        :items-per-page="itemsPerPage"
        :total="props.total"
        :sibling-count="SIBLING_COUNT"
        show-edges
        :disabled="props.disabled"
        :aria-label="i18n.t('Seitennavigation')"
        class="sph-pagination"
        :class="cn('@container/pagination relative flex flex-wrap-reverse items-center justify-end gap-x-4 gap-y-2', props.class)"
    >
        <span
            v-if="props.showCounter"
            class="text-muted-foreground text-sm"
        >{{ counterText }}</span>

        <PageSizeSelect
            v-if="showPageSizeSelect"
            v-model="itemsPerPage"
            :page-sizes="props.pageSizes"
            :disabled="props.disabled"
        />

        <div
            v-if="showNavigation"
            class="flex items-center gap-0"
        >
            <PaginationFirst
                v-if="branch !== 'list'"
                as-child
            >
                <SphButton
                    size="sm"
                    variant="transparent"
                    circle
                    :icon="ChevronFirst"
                    :aria-label="i18n.t('Erste Seite')"
                    :title="i18n.t('Erste Seite')"
                    :class="edgeClass"
                />
            </PaginationFirst>

            <PaginationPrev as-child>
                <SphButton
                    size="sm"
                    variant="transparent"
                    circle
                    :icon="ChevronLeft"
                    :aria-label="i18n.t('Vorherige Seite')"
                    :title="i18n.t('Vorherige Seite')"
                />
            </PaginationPrev>

            <!-- On the `responsive` branch both sit in the DOM and the container query swaps
                 them. `display: none` also takes the hidden one out of the accessibility tree. -->
            <PageList
                v-if="branch !== 'field'"
                :page="page"
                :animated="animateList"
                :class="listClass"
            />
            <PageInput
                v-if="branch !== 'list'"
                v-model="page"
                :page-count="pageCount"
                :disabled="props.disabled"
                :class="fieldClass"
            />

            <PaginationNext as-child>
                <SphButton
                    size="sm"
                    variant="transparent"
                    circle
                    :icon="ChevronRight"
                    :aria-label="i18n.t('Nächste Seite')"
                    :title="i18n.t('Nächste Seite')"
                />
            </PaginationNext>

            <PaginationLast
                v-if="branch !== 'list'"
                as-child
            >
                <SphButton
                    size="sm"
                    variant="transparent"
                    circle
                    :icon="ChevronLast"
                    :aria-label="i18n.t('Letzte Seite')"
                    :title="i18n.t('Letzte Seite')"
                    :class="edgeClass"
                />
            </PaginationLast>
        </div>
    </PaginationRoot>
</template>

<style scoped>
.sph-pagination {
    :deep(.ripple-container) {
        opacity: 0.25;
    }
}
</style>
